import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const ProgressBar = ({ value, color = 'bg-accent' }) => (
  <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
    <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
  </div>
);

const WorkerLeaderboard = ({ workerStats = [], weeklyStats = [], onWorkerClick }) => {
  const { t } = useLanguage();
  const [view, setView] = useState('today');
  const getBarColor = (r) => r >= 80 ? 'bg-emerald-500' : r >= 50 ? 'bg-amber-500' : 'bg-red-500';

  const sortedToday = [...workerStats]?.sort((a, b) => b?.completion_rate - a?.completion_rate);
  const sortedWeekly = [...weeklyStats]?.sort((a, b) => b?.tasks_this_week - a?.tasks_this_week);

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-foreground font-heading">{t('workerProductivity')}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{t('clickWorkerToViewHistory')}</p>
          </div>
        </div>
        <div className="flex gap-1">
          {[{ id: 'today', label: t('today') }, { id: 'week', label: t('thisWeek') }, { id: 'month', label: t('thisMonth') }]?.map(tab => (
            <button key={tab?.id} onClick={() => setView(tab?.id)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === tab?.id ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>{tab?.label}</button>
          ))}
        </div>
      </div>
      {view === 'today' && (
        <div className="divide-y divide-border">
          {sortedToday?.filter(w => w?.tasks_total > 0)?.map((worker, i) => (
            <div key={worker?.id} onClick={() => onWorkerClick?.(worker?.id)} className="px-5 py-4 hover:bg-muted/30 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-100 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-muted text-muted-foreground'}`}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                </div>
                <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-accent">{worker?.avatar}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{worker?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{worker?.trade} · {worker?.tasks_completed}/{worker?.tasks_total} {t('tasks')}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${worker?.completion_rate >= 80 ? 'text-emerald-600' : worker?.completion_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{worker?.completion_rate}%</p>
                      {worker?.tasks_overdue > 0 && <p className="text-xs text-orange-600 font-medium">{worker?.tasks_overdue} {t('overdue')}</p>}
                    </div>
                  </div>
                  <ProgressBar value={worker?.completion_rate} color={getBarColor(worker?.completion_rate)} />
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0" />
              </div>
            </div>
          ))}
          {sortedToday?.filter(w => w?.tasks_total > 0)?.length === 0 && (
            <div className="p-8 text-center"><p className="text-sm text-muted-foreground">{t('noTasksAssignedToday')}</p></div>
          )}
        </div>
      )}
      {(view === 'week' || view === 'month') && (
        <div className="divide-y divide-border">
          {sortedWeekly?.map((worker, i) => (
            <div key={worker?.id} onClick={() => onWorkerClick?.(worker?.id)} className="px-5 py-4 hover:bg-muted/30 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-100 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-muted text-muted-foreground'}`}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                </div>
                <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-accent">{worker?.avatar}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{worker?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{worker?.trade}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-foreground">{view === 'week' ? worker?.tasks_this_week : worker?.tasks_this_month}</p>
                      <p className="text-xs text-muted-foreground">{view === 'week' ? t('thisWeek') : t('thisMonth')}</p>
                    </div>
                  </div>
                  <div className="mt-1.5">
                    <ProgressBar value={worker?.avg_completion_rate} color={getBarColor(worker?.avg_completion_rate)} />
                    <p className="text-xs text-muted-foreground mt-0.5">{worker?.avg_completion_rate}% {t('avgCompletion')}</p>
                  </div>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkerLeaderboard;
